// Chat bubbles (SPEC §3).
//   <Bubble from="buddy" ch={ch} face e={1}>Hi.</Bubble>   left, glass bubble; `face` shows XFace (28px, accent ring)
//   <Bubble from="buddy" ch={ch}>Second line</Bubble>       no face: a 28px spacer keeps the column aligned
//   <Bubble from="user">The Q3 report.</Bubble>             right, cream bubble
//   <Bubble from="user" voice>Add the invoice.</Bubble>     said out loud: a small mic before the words
//   <VoiceBubble ms={6000} levels={clip.levels} />          a voice message on its way ("Turning it into text…")
//   <VoiceBubble ... failed onRetry={send} />              it didn't go through: "!" + "Didn't go through." + Try again
//   <TypingBubble ch={ch} />                               "{name} is typing" dots while a reply is on its way
import type { ComponentChildren, JSX } from 'preact';
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { XFace } from './XFace';
import { Icon } from './icons';
import s from './Bubble.module.css';

export interface BubbleProps {
  from: 'buddy' | 'user';
  ch?: CharId;
  /** Buddy: show the face (first bubble of a group). Without it a spacer of the same width is kept. */
  face?: boolean;
  /** Buddy: no face and no spacer (full-width bubble). */
  bare?: boolean;
  e?: number;
  faceSize?: number;
  /** CSS animation shorthand, e.g. 'xmRise 380ms ease-out both'. */
  anim?: string;
  /** Slightly stronger bubble (sheets, reward screens). */
  strong?: boolean;
  /** Max width in px (buddy 290, user 280 by default). */
  max?: number;
  /** User: the words were said out loud (hold to talk), shown with a small mic. */
  voice?: boolean;
  children: ComponentChildren;
  class?: string;
  style?: JSX.CSSProperties;
}

export function Bubble({ from, ch = 'margo', face, bare, e = 1, faceSize = 28, anim, strong, max, voice, children, class: cls, style }: BubbleProps) {
  if (from === 'user') {
    return (
      <p class={[s.user, cls].filter(Boolean).join(' ')} style={{ animation: anim, maxWidth: max ? `min(${max}px, 82%)` : undefined, ...style }}>
        {voice && <Icon name="mic" size={15} strokeWidth={2.2} color="#6B645A" label="Said out loud" class={s.said} />}
        {children}
      </p>
    );
  }
  return (
    <div class={[s.row, cls].filter(Boolean).join(' ')} style={{ animation: anim, ...style }}>
      {!bare && (face ? <XFace ch={ch} e={e} s={faceSize} ring="var(--acc)" alt="" /> : <span class={s.spacer} style={{ width: faceSize }} aria-hidden="true" />)}
      <p class={[s.buddy, strong && s.strong].filter(Boolean).join(' ')} style={{ maxWidth: max ? `min(${max}px, 100%)` : undefined }}>
        {face && <span class="xm-sr-only">{C[ch].name}: </span>}
        {children}
      </p>
    </div>
  );
}

export function TypingBubble({ ch = 'margo', faceSize = 28, face = true }: { ch?: CharId; faceSize?: number; face?: boolean }) {
  return (
    <div class={s.row} role="status" aria-label={`${C[ch].name} is typing`} style={{ animation: 'xmRise 300ms ease-out both' }}>
      {face ? <XFace ch={ch} e={3} s={faceSize} ring="var(--acc)" alt="" /> : <span class={s.spacer} style={{ width: faceSize }} />}
      <p class={s.buddy} style={{ display: 'flex', gap: 5, alignItems: 'center', height: 45 }} aria-hidden="true">
        <span class={s.dot} /><span class={s.dot} style={{ animationDelay: '150ms' }} /><span class={s.dot} style={{ animationDelay: '300ms' }} />
      </p>
    </div>
  );
}

const BARS = [0.25, 0.5, 0.8, 0.4, 1, 0.65, 0.35, 0.85, 0.55, 0.3, 0.7, 0.9, 0.45, 0.25, 0.6, 0.8, 0.4, 0.55, 0.3, 0.2];
const len = (ms: number) => { const t = Math.max(1, Math.round(ms / 1000)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

/** The user's voice message while it is on its way (feedback 11-03, canvas fb1103 row 8): right after release, a cream
 *  bubble on the right with a mic, the clip's loudness and its length, and "Turning it into text…" under it. The
 *  server's transcript then takes its place as the user's message. `failed`: a red "!" beside it and "Didn't go
 *  through." with Try again; the clip is kept, so nothing has to be said twice. */
export function VoiceBubble({ ms, levels, failed, onRetry, anim }: { ms: number; levels?: number[]; failed?: boolean; onRetry?: () => void; anim?: string }) {
  const bars = levels && levels.length ? levels : BARS;
  const length = len(ms);
  return (
    <div class={s.voiceWrap} style={{ animation: anim }}>
      <div class={s.voiceRow}>
        {failed && <span class={s.voiceFail} aria-hidden="true">!</span>}
        <div class={s.voice} role="img" aria-label={`Your voice message, ${length}`} data-state={failed ? 'failed' : 'sending'}>
          <Icon name="mic" size={16} strokeWidth={2.2} color="#6B645A" />
          <span class={s.voiceBars} aria-hidden="true">
            {bars.map((v, i) => <span key={i} style={{ height: 4 + Math.round(Math.max(0, Math.min(1, v)) * 16), animationDelay: `${i * 60}ms` }} />)}
          </span>
          <span class={s.voiceLen}>{length}</span>
        </div>
      </div>
      {failed
        ? (
          <div class={s.voiceStatus} role="status">
            <span class={s.voiceErr}>Didn't go through.</span>
            <button type="button" class={s.voiceRetry} onClick={onRetry}>Try again</button>
          </div>
        )
        : <span class={s.voiceNote} role="status">Turning it into text…</span>}
    </div>
  );
}
